<template>
  <BaseDriverContactSection
    :id="id"
    :driver-contact="driverContact"
    :editable="editable"
    @save="onSaveDriverContact"
  />
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useToast } from '@rygen/rygen-ui/composables';
import { DomainReference, Loadable, Optional } from '@rygen/rygen-ui/model';

import api from '@/api';
import { DriverContact, Shipment } from '@/model';

import BaseDriverContactSection, {
  DriverContactPayload,
} from '@/components/shipment/driverContact/BaseDriverContactSection.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipment: {
    type: Object as PropType<Shipment>,
    default: () => new Shipment(),
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, shipment, editable } = toRefs(props);

const { t } = useI18n();
const toast = useToast();
const router = useRouter();

const driverContact: Ref<Loadable<DriverContact>> = ref({ loading: false });

const shipmentId: Ref<Optional<number>> = toRef(() => shipment.value?.id);

const owningClient = computed(() => shipment.value?.owner);

const accessToken: Ref<string | undefined> = computed(() => {
  return router.currentRoute.value.query.accessToken as string | undefined;
});

const loadDriverContact = async (shipmentId: number) => {
  if (!accessToken.value) {
    return;
  }

  try {
    driverContact.value.loading = true;
    driverContact.value.data =
      await api.carrierPortal.loadDriverContactForShipment(
        shipmentId,
        accessToken.value,
      );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    if (axios.isAxiosError(e) && e.response?.status === 404) {
      // No driver contact is fine
      return;
    }

    driverContact.value.error = e;
    console.error('Error loading driver contact', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t(t('shipments.driverContact')),
      }),
      error: e,
    });
  } finally {
    driverContact.value.loading = false;
  }
};

const onSaveDriverContact = async ({
  driverContact: newVal,
  onSuccessCallback,
  onErrorCallback,
}: DriverContactPayload) => {
  if (!shipmentId.value || !accessToken.value) {
    return;
  }

  const isUpdate = !!newVal.id;

  try {
    driverContact.value.loading = true;

    newVal.shipmentId = shipmentId.value;
    driverContact.value.data = isUpdate
      ? await api.carrierPortal.updateDriverContactForShipment(
          shipmentId.value,
          newVal,
          accessToken.value,
        )
      : await api.carrierPortal.createDriverContactForShipment(
          shipmentId.value,
          newVal,
          accessToken.value,
        );

    onSuccessCallback?.();
  } catch (e) {
    onErrorCallback?.(e);
    if (axios.isCancel(e)) {
      return;
    }

    driverContact.value.error = e;
    console.error('Error upserting driver contact', e);
    toast.addError({
      detail: t(isUpdate ? 'errors.updatingParam' : 'errors.savingParam', {
        param: t(t('shipments.driverContact')),
      }),
      error: e,
    });
  } finally {
    driverContact.value.loading = false;
  }
};

watch(
  shipment,
  (newVal, oldVal) => {
    const isSameId = newVal?.id === oldVal?.id;
    const isSameOwner = newVal?.owner?.id === oldVal?.owner?.id;
    if (!newVal?.id || (isSameId && !isSameOwner)) {
      return;
    }

    loadDriverContact(newVal.id);
  },
  { immediate: true },
);

watch(
  () => owningClient.value?.id,
  (newVal: DomainReference['id']) => {
    if (!newVal || !shipmentId.value) {
      return;
    }

    loadDriverContact(shipmentId.value);
  },
  { immediate: true },
);
</script>
